import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { heroImages, teamMembers, founderImage } from '../data/mockData';
import SEOHead from '../components/SEOHead';
import { ArrowRight } from 'lucide-react';

const HouseBilingual = () => {
  const { language, t } = useLanguage();
  const house = translations.house;
  const nav = translations.nav;
  
  const langPrefix = `/${language}`;
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };

  return (
    <div>
      <SEOHead 
        title={t(house.seoTitle)}
        description={t(house.seoDescription)}
      />
      
      {/* Spirit Section */}
      <section className="section-padding-large">
        <div className="container">
          <div className="grid-two-column" style={{ gap: '100px' }}>
            <div>
              <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
                {t(house.spiritSubtitle)}
              </p>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>
                {t(house.spiritTitle)}
              </h2>
              <p className="body-regular" style={{ 
                marginBottom: '24px',
                color: 'var(--text-secondary)',
                lineHeight: 1.8
              }}>
                {t(house.spiritP1)}
              </p>
              <p className="body-regular" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: 1.8
              }}>
                {t(house.spiritP2)}
              </p>
            </div>
            <div>
              <img 
                src={heroImages.secondary}
                alt={t(house.spiritTitle)}
                style={{
                  width: '100%',
                  height: '600px',
                  objectFit: 'cover',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(house.leadershipSubtitle)}
            </p>
            <h2 className="heading-1">
              {t(house.leadershipTitle)}
            </h2>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
            gap: '60px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Nidale Barret */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '100%',
                maxWidth: '350px',
                height: '450px',
                margin: '0 auto 32px',
                overflow: 'hidden'
              }}>
                <img 
                  src={founderImage}
                  alt="Nidale Barret"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 15%',
                    filter: 'grayscale(100%) contrast(1.2) brightness(0.95)'
                  }}
                />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                Nidale Barret
              </h3>
              <p className="meta-text" style={{ 
                marginBottom: '20px',
                color: 'var(--gold-accent)'
              }}>
                {language === 'en' ? 'Founder' : 'Fondatrice'}
              </p>
              <p className="body-regular" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: 1.7
              }}>
                {t(house.nidaleBio)}
              </p>
              <div style={{ marginTop: '20px' }}>
                <Link to={getRoute('founder')} className="btn-icon" style={{ color: 'var(--gold-accent)' }}>
                  {t(house.readBio)}
                  <ArrowRight size={16} style={{ marginLeft: '8px' }} />
                </Link>
              </div>
            </div>

            {/* Camille Carron */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '100%',
                maxWidth: '350px',
                height: '450px',
                margin: '0 auto 32px',
                overflow: 'hidden'
              }}>
                <img 
                  src={teamMembers[1].image}
                  alt="Camille Carron"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    filter: 'grayscale(100%) contrast(1.35) brightness(0.9)'
                  }}
                />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                Camille Carron
              </h3>
              <p className="meta-text" style={{ 
                marginBottom: '20px',
                color: 'var(--gold-accent)'
              }}>
                {language === 'en' ? 'Director of Business Development' : 'Directrice du développement'}
              </p>
              <p className="body-regular" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: 1.7
              }}>
                {t(house.camilleBio)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Extended Network Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
              {t(house.networkTitle)}
            </p>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              fontStyle: 'italic'
            }}>
              {t(house.networkDesc)}
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(house.philosophySubtitle)}
            </p>
            <h2 className="heading-1">
              {t(house.philosophyTitle)}
            </h2>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: '48px'
          }}>
            <div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                {t(house.parisianTitle)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t(house.parisianDesc)}
              </p>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                {t(house.humanTitle)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t(house.humanDesc)}
              </p>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                {t(house.emotionalTitle)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t(house.emotionalDesc)}
              </p>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                {t(house.excellenceTitle)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t(house.excellenceDesc)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
                {t(house.howWeWorkSubtitle)}
              </p>
              <h2 className="heading-1">
                {t(house.howWeWorkTitle)}
              </h2>
            </div>
            
            <div style={{ marginBottom: '48px' }}>
              {[
                { num: '01', title: house.step1Title, desc: house.step1Desc },
                { num: '02', title: house.step2Title, desc: house.step2Desc },
                { num: '03', title: house.step3Title, desc: house.step3Desc },
                { num: '04', title: house.step4Title, desc: house.step4Desc }
              ].map((step, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '32px',
                  marginBottom: idx < 3 ? '40px' : 0
                }}>
                  <span className="meta-text" style={{ color: 'var(--gold-accent)', whiteSpace: 'nowrap' }}>{step.num} —</span>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                      {t(step.title)}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {t(step.desc)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="hero-medium" style={{ 
              marginBottom: '32px',
              fontStyle: 'italic',
              lineHeight: 1.5
            }}>
              {t(house.quote)}
            </h2>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              — Nidale Barret
            </p>
          </div>
        </div>
      </section>

      {/* SEO Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '12px',
            lineHeight: 1.7,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {t(house.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default HouseBilingual;
